import {CrosspointDataService} from "../types/crosspoint-data-service.interface";
import { Viewport } from "../types/viewport.interface";
import {InMemoryCrosspointDataServiceConfiguration} from "../types/in-memory-data-service-configuration.interface";
import {Axis} from "../../axis";

/**
 * A service implementation that provides in-memory data management for a crosspoint data structure.
 * This class conforms to the `CrosspointDataService` interface.
 * It fetches data dynamically using a generator function provided in its configuration.
 *
 * @template TXValue - The type of the x-axis values.
 * @template TYAxis - The type of the y-axis values.
 * @template TItem - The type of the items being managed by the service.
 */
export class InMemoryCrosspointDataService<TXValue, TYAxis, TItem> implements CrosspointDataService<TItem> {

    /**
     * Constructs an instance of the service with the specified configuration, x-axis, and y-axis.
     *
     * @param {InMemoryCrosspointDataServiceConfiguration<TXValue, TYAxis, TItem>} config - The configuration settings for the in-memory crosspoint data service.
     * @param {Axis<TXValue, any>} xAxis - The x-axis instance used by the service.
     * @param {Axis<TYAxis, any>} yAxis - The y-axis instance used by the service.
     */
    constructor(
        private config: InMemoryCrosspointDataServiceConfiguration<TXValue, TYAxis, TItem>,
        private xAxis: Axis<TXValue, any>,
        private yAxis: Axis<TYAxis, any>
    ) {
    }

    /**
     * Fetches an array of data items based on the given viewport.
     *
     * @param {Viewport} viewport - The viewport defining the data range to fetch.
     * @return {Promise<TItem[]>} A promise that resolves to an array of data items.
     */
    fetchData(viewport: Viewport): Promise<TItem[]> {
        const items = this.config.generator.call(this, viewport, this.xAxis, this.yAxis);
        return Promise.resolve(items);
    }

    /**
     * Cancels all currently pending requests in the system.
     * This method is typically used to abort operations that are no longer needed
     * or to free up resources tied to pending requests.
     *
     * @return {void} No value is returned.
     */
    cancelPendingRequests(): void {
        // nothing to do
    }

}